import type { LucideIcon } from '@parrot-software/design-system/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import { Receipt, SquareMenu, Users } from '@parrot-software/design-system/icons';

type NextStep = {
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  route: string;
};

const NEXT_STEPS: NextStep[] = [
  {
    icon: Users,
    titleKey: 'steps.nextSteps.items.personnel.title',
    descriptionKey: 'steps.nextSteps.items.personnel.description',
    route: '/personnel',
  },
  {
    icon: Receipt,
    titleKey: 'steps.nextSteps.items.billing.title',
    descriptionKey: 'steps.nextSteps.items.billing.description',
    route: '/billing',
  },
  {
    icon: SquareMenu,
    titleKey: 'steps.nextSteps.items.menu.title',
    descriptionKey: 'steps.nextSteps.items.menu.description',
    route: '/menu',
  },
];

type NextStepsStepProps = {
  onStart: () => void;
};

/**
 * Renders a single next step row with semantic icon, title and description.
 */
const NextStepRow = ({
  descriptionKey,
  Icon,
  titleKey,
}: {
  descriptionKey: string;
  Icon: LucideIcon;
  titleKey: string;
}) => {
  const { t } = useTranslation('onboarding');

  return (
    <div className="flex items-start gap-2">
      <Icon className="shrink-0 text-primary" size={24} />
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold leading-5 text-foreground">{t(titleKey)}</span>
        <span className="text-sm font-normal leading-5 text-foreground">{t(descriptionKey)}</span>
      </div>
    </div>
  );
};

/**
 * Wizard 8b: Next steps screen — shows actionable items after wizard completion.
 * Each item links to a section of the admin portal.
 */
export const NextStepsStep = ({ onStart }: NextStepsStepProps) => {
  const { t } = useTranslation('onboarding');

  return (
    <div className="flex flex-1 flex-col gap-7 py-3">
      <div className="flex flex-col gap-1 text-center">
        <h2 className="whitespace-pre-line text-xl font-semibold leading-8 text-foreground">
          {t('steps.nextSteps.title')}
        </h2>
        <p className="text-sm leading-5 text-muted-foreground">{t('steps.nextSteps.subtitle')}</p>
      </div>

      <div className="flex flex-col gap-5 py-3">
        {NEXT_STEPS.map((step) => (
          <NextStepRow
            Icon={step.icon}
            descriptionKey={step.descriptionKey}
            key={step.route}
            titleKey={step.titleKey}
          />
        ))}
      </div>

      <div className="mt-auto flex flex-col items-stretch pt-4 lg:items-center">
        <Button className="w-full lg:w-auto" onClick={onStart} type="button">
          {t('steps.nextSteps.startButton')}
        </Button>
      </div>
    </div>
  );
};
